<template>
  <div class="device_control_page_wraper rgbw_editor">
    <div class="close_img_top">
      <img src="~assets/img/controlpublic/control-guanbi-chahao.png" @click="$closeControlToast" alt="关闭" />
    </div>
    <div class="devce_name z_ellipsis1">{{ objData.remark }}</div>

    <section class="rgbw_color_main">
      <div ref="colorWheel" class="rgbw_color_wheel"
           @click.prevent="selectWheelOnce">
        <span class="rgbw_wheel_cursor" :style="wheelCursorStyle"></span>
      </div>
      <div class="rgbw_color_side">
        <div class="rgbw_rgb_values">
          <label v-for="(key, index) in rgbKeys" :key="key">
            <small>{{ rgbLabels[index] }}</small>
            <input type="number" min="0" max="255" :value="controlData[key]"
                   @input="updateRgbValue(key, $event.target.value)"
                   @change="commitStatic" />
          </label>
        </div>
        <div class="rgbw_presets">
          <button v-for="preset in colorPresets" :key="preset.name" type="button"
                  :title="preset.name" :style="{ background: preset.color }"
                  @click="applyColorPreset(preset)"></button>
        </div>
      </div>
    </section>

    <section class="rgbw_brightness_panel">
      <label>
        <span><i class="rgbw_color_icon" :style="{ background: draftColor }"></i>彩光亮度</span>
        <input type="range" min="0" max="100" v-model.number="picker.v"
               :style="{ '--range-color': valueColor }" @input="applyPickerValues" @change="commitStatic" />
        <em>{{ picker.v }}%</em>
      </label>
      <label>
        <span><i class="rgbw_white_icon"></i>白光亮度</span>
        <input type="range" min="0" max="255" v-model.number="controlData.openStateValue2"
               @change="commitStatic" />
        <em>{{ percent(controlData.openStateValue2) }}%</em>
      </label>
    </section>

    <section ref="modePicker" class="rgbw_mode">
      <span class="rgbw_panel_label">灯光模式</span>
      <button type="button" class="rgbw_mode_trigger" @click="toggleModePicker">
        <span>{{ currentModeLabel }}</span><span>{{ modeOpen ? '▴' : '▾' }}</span>
      </button>
      <div v-if="modeOpen" class="rgbw_mode_grid">
        <button v-for="mode in modes" :key="mode.value" type="button"
                :class="{ active: controlData.openStateValue3 === mode.value }"
                @click="selectMode(mode.value)">{{ mode.label }}</button>
      </div>
    </section>

    <div class="device_control_btn public_control_btn rgbw_power"
         :class="{ public_control_btn_on: isOn }" role="button" tabindex="0"
         @click="togglePower" @keyup.enter="togglePower">
      {{ isOn ? '已打开' : '已关闭' }}
    </div>
  </div>
</template>

<script>
import { controlBtnRequestHandle } from "components/content/control-toast/control-toast-classify/device-public-js";

const clampByte = value => Math.max(0, Math.min(255, Math.round(Number(value) || 0)));
const byteHex = value => clampByte(value).toString(16).padStart(2, "0");
const hsvToRgb = ({ h, s, v }) => {
  const saturation = s / 100;
  const brightness = v / 100;
  const chroma = brightness * saturation;
  const section = ((h % 360) + 360) % 360 / 60;
  const x = chroma * (1 - Math.abs(section % 2 - 1));
  const values = section < 1 ? [chroma, x, 0] : section < 2 ? [x, chroma, 0] :
    section < 3 ? [0, chroma, x] : section < 4 ? [0, x, chroma] :
      section < 5 ? [x, 0, chroma] : [chroma, 0, x];
  const match = brightness - chroma;
  return values.map(value => clampByte((value + match) * 255));
};
const rgbToHsv = (red, green, blue) => {
  const values = [red, green, blue].map(value => clampByte(value) / 255);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const delta = max - min;
  let hue = 0;
  if (delta && max === values[0]) hue = 60 * (((values[1] - values[2]) / delta) % 6);
  else if (delta && max === values[1]) hue = 60 * ((values[2] - values[0]) / delta + 2);
  else if (delta) hue = 60 * ((values[0] - values[1]) / delta + 4);
  return { h: Math.round((hue + 360) % 360), s: Math.round(max ? delta / max * 100 : 0), v: Math.round(max * 100) };
};

export default {
  props: {
    objData: { type: Object, required: true },
    realtime: { type: Boolean, default: false }
  },
  data() {
    const source = this.realtime ? { ...this.objData } : this.objData;
    source.openState = clampByte(source.openState);
    source.openStateValue = clampByte(source.openStateValue);
    source.openStateValue1 = clampByte(source.openStateValue1);
    source.openStateValue2 = clampByte(source.openStateValue2);
    source.openStateValue3 = Math.max(0, Math.min(7, Number(source.openStateValue3) || 0));
    const picker = rgbToHsv(source.openState, source.openStateValue, source.openStateValue1);
    return {
      controlData: source,
      modeOpen: false,
      picker,
      lastColorBrightness: picker.v > 0 ? picker.v : 100,
      rgbLabels: ["R", "G", "B"],
      rgbKeys: ["openState", "openStateValue", "openStateValue1"],
      lastStatic: null,
      channels: [
        { key: "openState", shortName: "R", name: "红光", dotClass: "red" },
        { key: "openStateValue", shortName: "G", name: "绿光", dotClass: "green" },
        { key: "openStateValue1", shortName: "B", name: "蓝光", dotClass: "blue" },
        { key: "openStateValue2", shortName: "W", name: "白光", dotClass: "white" }
      ],
      colorPresets: [
        { name: "红", color: "#ff3b30", rgb: [255, 0, 0] },
        { name: "绿", color: "#34c759", rgb: [0, 255, 0] },
        { name: "蓝", color: "#0a84ff", rgb: [0, 0, 255] },
        { name: "黄", color: "#ffd60a", rgb: [255, 255, 0] },
        { name: "青", color: "#32d7d2", rgb: [0, 255, 255] },
        { name: "紫", color: "#bf5af2", rgb: [255, 0, 255] }
      ],
      modes: [
        { value: 0, label: "静态调光" }, { value: 5, label: "RGB 呼吸" },
        { value: 1, label: "红光呼吸" }, { value: 2, label: "绿光呼吸" },
        { value: 3, label: "蓝光呼吸" }, { value: 4, label: "白光呼吸" },
        { value: 6, label: "黄光呼吸" }, { value: 7, label: "紫光呼吸" }
      ]
    };
  },
  mounted() {
    document.addEventListener("mousedown", this.closeModePickerOutside);
    document.addEventListener("touchstart", this.closeModePickerOutside);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.closeModePickerOutside);
    document.removeEventListener("touchstart", this.closeModePickerOutside);
  },
  computed: {
    isOn() {
      return this.controlData.openState > 0 || this.controlData.openStateValue > 0 ||
        this.controlData.openStateValue1 > 0 || this.controlData.openStateValue2 > 0 ||
        this.controlData.openStateValue3 > 0;
    },
    draftRgb() {
      return hsvToRgb(this.picker);
    },
    draftColor() {
      return `#${this.draftRgb.map(byteHex).join("")}`;
    },
    valueColor() {
      const color = hsvToRgb({ h: this.picker.h, s: this.picker.s, v: 100 });
      return `rgb(${color.join(",")})`;
    },
    wheelCursorStyle() {
      const radians = this.picker.h * Math.PI / 180;
      const radius = this.picker.s * 0.46;
      return {
        left: `${50 + Math.sin(radians) * radius}%`,
        top: `${50 - Math.cos(radians) * radius}%`,
        background: this.draftColor
      };
    },
    currentModeLabel() {
      const mode = this.modes.find(item => item.value === this.controlData.openStateValue3);
      return mode ? mode.label : "静态调光";
    }
  },
  watch: {
    objData: {
      deep: true,
      handler(newValue) {
        if (!this.realtime || !newValue) return;
        this.syncRealtimeState(newValue);
      }
    }
  },
  methods: {
    syncRealtimeState(source) {
      const values = [
        clampByte(source.openState),
        clampByte(source.openStateValue),
        clampByte(source.openStateValue1),
        clampByte(source.openStateValue2)
      ];
      const mode = Math.max(0, Math.min(7, Number(source.openStateValue3) || 0));

      this.controlData.openState = values[0];
      this.controlData.openStateValue = values[1];
      this.controlData.openStateValue1 = values[2];
      this.controlData.openStateValue2 = values[3];
      this.controlData.openStateValue3 = mode;
      this.picker = rgbToHsv(values[0], values[1], values[2]);

      if (this.picker.v > 0) this.lastColorBrightness = this.picker.v;
      if (values.some(value => value > 0)) this.lastStatic = values.slice();
    },
    percent(value) {
      return Math.round(clampByte(value) * 100 / 255);
    },
    submit() {
      if (this.realtime) controlBtnRequestHandle(this.controlData, this.objData);
    },
    commitStatic() {
      this.channels.forEach(channel => {
        this.controlData[channel.key] = clampByte(this.controlData[channel.key]);
      });
      this.controlData.openStateValue3 = 0;
      this.submit();
    },
    updateRgbValue(key, value) {
      this.controlData[key] = clampByte(value);
      this.picker = rgbToHsv(this.controlData.openState, this.controlData.openStateValue,
        this.controlData.openStateValue1);
      this.controlData.openStateValue3 = 0;
    },
    selectWheel(event) {
      const point = event.touches ? event.touches[0] : event;
      const rect = this.$refs.colorWheel.getBoundingClientRect();
      const radius = rect.width / 2;
      const x = point.clientX - rect.left - radius;
      const y = point.clientY - rect.top - radius;
      const distance = Math.min(Math.sqrt(x * x + y * y), radius);
      this.picker.h = Math.round((Math.atan2(y, x) * 180 / Math.PI + 450) % 360);
      this.picker.s = Math.round(distance / radius * 100);
      this.applyPickerValues();
    },
    selectWheelOnce(event) {
      if (this.picker.v <= 0) this.picker.v = this.lastColorBrightness;
      this.selectWheel(event);
      this.commitPickerColor();
    },
    applyPickerValues() {
      [this.controlData.openState, this.controlData.openStateValue,
        this.controlData.openStateValue1] = this.draftRgb;
      if (this.picker.v > 0) this.lastColorBrightness = this.picker.v;
      this.controlData.openStateValue3 = 0;
    },
    commitPickerColor() {
      this.applyPickerValues();
      this.commitStatic();
    },
    applyColorPreset(preset) {
      const presetPicker = rgbToHsv(...preset.rgb);
      presetPicker.v = this.picker.v > 0 ? this.picker.v : this.lastColorBrightness;
      this.picker = presetPicker;
      this.applyPickerValues();
      this.commitStatic();
    },
    selectMode(value) {
      this.controlData.openStateValue3 = value;
      this.modeOpen = false;
      this.submit();
    },
    toggleModePicker() {
      this.modeOpen = !this.modeOpen;
    },
    closeModePickerOutside(event) {
      if (this.modeOpen && this.$refs.modePicker && !this.$refs.modePicker.contains(event.target)) {
        this.modeOpen = false;
      }
    },
    togglePower() {
      const keys = this.channels.map(channel => channel.key);
      if (this.isOn) {
        this.lastStatic = keys.map(key => this.controlData[key]);
        if (this.picker.v > 0) this.lastColorBrightness = this.picker.v;
        keys.forEach(key => { this.controlData[key] = 0; });
        this.controlData.openStateValue3 = 0;
        this.picker.v = 0;
      } else {
        const values = this.lastStatic || [255, 255, 255, 0];
        keys.forEach((key, index) => { this.controlData[key] = values[index]; });
        this.picker = rgbToHsv(values[0], values[1], values[2]);
        if (this.picker.v > 0) this.lastColorBrightness = this.picker.v;
      }
      this.submit();
    }
  }
};
</script>

<style scoped>
.rgbw_editor { width: 460px; height: 514px; align-items: stretch; cursor: default; }
.rgbw_editor > .close_img_top { height: 24px; flex-shrink: 0; }
.rgbw_editor > .close_img_top img { width: 21px; height: 21px; cursor: pointer; }
.rgbw_editor > .devce_name { width: 100%; height: 32px; line-height: 32px; margin-bottom: 12px; color: #f5f5f5; font-size: 20px; text-align: center; }
.rgbw_color_main { height: 224px; margin: 0 8px 10px; padding: 10px 28px; display: flex; align-items: center; justify-content: center; gap: 30px; box-sizing: border-box; border: 1px solid rgba(255,255,255,.14); border-radius: 7px; flex-shrink: 0; }
.rgbw_color_wheel { position: relative; width: 196px; height: 196px; flex: 0 0 auto; border: 0; border-radius: 50%; box-sizing: border-box; background: linear-gradient(180deg, rgba(255,255,255,.1) 0%, transparent 42%, rgba(0,0,0,.1) 100%), radial-gradient(circle, #fff 0%, rgba(255,255,255,.94) 4%, rgba(255,255,255,0) 70%), conic-gradient(#f44336, #ffeb3b, #4caf50, #00bcd4, #3f51b5, #9c27b0, #f44336); box-shadow: inset 0 0 0 1px rgba(0,0,0,.22), inset 0 -7px 12px rgba(0,0,0,.08), 0 5px 10px rgba(0,0,0,.2); cursor: pointer; touch-action: manipulation; }
.rgbw_wheel_cursor { position: absolute; width: 17px; height: 17px; border: 2px solid #fff; border-radius: 50%; box-sizing: border-box; transform: translate(-50%,-50%); box-shadow: 0 1px 5px rgba(0,0,0,.8); pointer-events: none; }
.rgbw_color_side { width: 118px; }
.rgbw_rgb_values { display: grid; grid-template-columns: repeat(3, 4ch); justify-content: center; gap: 5px; margin-bottom: 18px; }
.rgbw_rgb_values label { width: 4ch; height: 42px; display: flex; flex-direction: column; align-items: center; justify-content: center; box-sizing: border-box; border: 1px solid rgba(255,255,255,.1); border-radius: 5px; background: rgba(8,12,22,.25); overflow: hidden; }
.rgbw_rgb_values small { color: #858b99; font-size: 10px; }
.rgbw_rgb_values input { width: 4ch; max-width: 4ch; height: 20px; padding: 0; border: 0; outline: 0; background: transparent; color: #f2f3f6; font-size: 13px; text-align: center; appearance: textfield; }
.rgbw_rgb_values input::-webkit-inner-spin-button, .rgbw_rgb_values input::-webkit-outer-spin-button { margin: 0; appearance: none; }
.rgbw_presets { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.rgbw_presets button { width: 26px; height: 26px; justify-self: center; padding: 0; border: 0; border-radius: 50%; box-shadow: none; cursor: pointer; }
.rgbw_brightness_panel { margin: 0 8px 10px; padding: 7px 16px; border: 1px solid rgba(255,255,255,.14); border-radius: 7px; flex-shrink: 0; }
.rgbw_brightness_panel label { height: 37px; display: grid; grid-template-columns: 86px 1fr 42px; gap: 10px; align-items: center; color: #dfe1e7; font-size: 12px; }
.rgbw_brightness_panel label > span { display: flex; align-items: center; gap: 7px; }
.rgbw_brightness_panel input { width: 100%; height: 5px; margin: 0; accent-color: var(--range-color, #f5f5f5); cursor: pointer; }
.rgbw_brightness_panel input::-webkit-slider-thumb { box-shadow: none; filter: none; }
.rgbw_brightness_panel input::-moz-range-thumb { box-shadow: none; filter: none; }
.rgbw_brightness_panel em { color: #a8adb8; font-style: normal; text-align: right; }
.rgbw_color_icon, .rgbw_white_icon { width: 10px; height: 10px; display: inline-block; border-radius: 50%; box-shadow: none; }
.rgbw_white_icon { background: #f5f5f5; }
.rgbw_mode { position: relative; height: 52px; margin: 0 8px 10px; padding: 0 14px; display: flex; justify-content: space-between; align-items: center; box-sizing: border-box; border: 1px solid rgba(255,255,255,.14); border-radius: 7px; flex-shrink: 0; }
.rgbw_panel_label { color: #dfe1e7; font-size: 13px; }
.rgbw_mode_trigger { width: 168px; height: 34px; padding: 0 12px; display: flex; justify-content: space-between; align-items: center; border: 1px solid rgba(255,255,255,.18); border-radius: 5px; background: rgba(8,12,22,.35); color: #fff; font-size: 13px; cursor: pointer; }
.rgbw_mode_grid { position: absolute; z-index: 3; right: 14px; bottom: 43px; width: 250px; padding: 8px; display: grid; grid-template-columns: 1fr 1fr; gap: 6px; border: 1px solid #3b414f; border-radius: 7px; background: #252933; box-shadow: 0 8px 24px rgba(0,0,0,.45); }
.rgbw_mode_grid button { height: 32px; border: 1px solid transparent; border-radius: 7px; background: #303542; color: #dfe1e7; font-size: 12px; cursor: pointer; }
.rgbw_mode_grid button.active { color: #fff; border-color: var(--primary-color); background: rgba(78,110,242,.3); }
.rgbw_power { display: block; width: calc(100% - 56px); height: 40px; line-height: 28px; box-sizing: border-box; margin: 4px auto 14px; flex-shrink: 0; font-size: 16px; cursor: pointer; }
@media (max-width: 620px) {
  .rgbw_editor { width: 86vw; }
  .rgbw_color_main { gap: 16px; padding: 10px 14px; }
  .rgbw_color_wheel { width: 180px; height: 180px; }
}
</style>
